import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMenuCapability } from '@/lib/auth/permissions';
import { particleSizeFor } from '@/components/hero/orbScene';
import { useStoredState } from '@/lib/utils/useStoredState';
import { ORB_DESIGN_DEFAULT, toLook, useOrbDesign, useSaveOrbDesign } from '@/pages/aiagent/orbDesign';
import { pickOrbLine, useOrbLines } from '@/pages/aiagent/orbLines';
import { useOrbAnchor } from '@/pages/aiagent/orbAnchor';

/**
 * AI Agent 를 여는 <b>히어로 오브</b> — 화면에 늘 떠 있다(사용자 지시 2026-09-17).
 *
 * <h2>왜 헤더에서 내려왔나</h2>
 *
 * 예전에는 헤더의 「AI Agent」 알약이 이 문이었다. 헤더는 폭이 정해져 있어 바로가기·직원찾기·?·
 * 개선사항 요청과 자리를 다퉜고, 좁은 화면에서는 이름이 잘렸다. <b>떠 있는 동그라미</b>는 폭을
 * 다투지 않는다. 헤더의 알약은 그래서 지웠다 — 같은 문이 둘이면 어느 쪽이 무엇인지 설명해야 한다.
 *
 * <h2>그림은 실험실의 그 씬이다</h2>
 *
 * 파란 원 안의 말풍선은 어느 사이트에나 있는 「고객센터 챗봇」의 얼굴이라 격이 맞지 않았다
 * (사용자 지적). 새로 그리지 않고 {@link HeroOrb} 를 그대로 넣는다 — 실험실 디자인 탭의
 * 히어로이고, 로그인 화면이 쓰려고 `components/hero/` 로 나와 있는 그 씬이다.
 *
 * <p>배경(스카이돔)과 둘레 섬광만 끈다. <b>알갱이는 켠 채다</b> — 구체 표면의 그 티끌이 살결이라
 * 끄면 매끈한 공 하나가 남는다(한 번 통째로 껐다가 되살렸다).
 *
 * <p><b>이 씬은 공짜가 아니다.</b> 매 프레임 유체 시뮬 + 알갱이 판 + bloom 을 돌린다. 로그인
 * 화면이 이걸 둘 수 있었던 이유는 «하루 한 번 열고 떠나는 자리» 라서였는데, 이 버튼은 반대로
 * <b>모든 화면에 늘 떠 있다.</b> 그래서 비용을 셋으로 묶는다: {@code lazy} 로 따로 받고,
 * 도크가 열려 있는 동안은 씬을 통째로 언마운트하고, 못 도는 기기에서는 아예 돌리지 않는다
 * ({@link CAN_RUN_SCENE}).
 *
 * <h2>색과 자리는 회사에 한 벌이다</h2>
 *
 * 관리자가 정해 모두가 본다({@link useOrbDesign}). 편집 권한이 있는 사람이 끌어 놓으면 그 자리가
 * 곧 저장이고, 전 직원의 화면에서 같은 자리다.
 *
 * <p><b>권한이 없어도 끌린다</b> (사용자 지시 2026-10-28). 오른쪽 아래에 박힌 채 그 밑의 버튼을
 * 가렸는데, 끄는 사람은 자기 권한을 모른다. 그런 사람이 놓은 자리는 <b>그 브라우저에만</b>
 * 남는다({@link PERSONAL_AT_KEY}) — 회사 자리를 바꾸지는 않는다.
 *
 * <p><b>누르자마자 끌린다</b>({@link DRAG_SLOP} · 2026-09-29). 꾹 누르는
 * 시간은 없다 — 누른 것과 끈 것은 <b>움직인 거리</b>로만 가른다.
 *
 * <h2>여는 것은 <b>기존 우측 도크</b>다</h2>
 *
 * 누르면 떠 있는 말풍선 창이 아니라 {@link AiAgentDock} 이 열린다(사용자 지시). 본문 위에 얹히는
 * 창은 <b>보면서 묻는다</b>는 목적과 어긋난다 — 가려진 표를 보려면 창을 다시 닫아야 한다. 도크는
 * 겹치지 않고 밀기 때문에 대화와 화면이 함께 보인다.
 *
 * <p>그래서 <b>여는 조건도 도크의 것을 그대로 따른다</b>({@link AppLayout} 의 `dockAvailable`).
 * 도크가 설 수 없는 자리 — 홈·좁은 화면(&lt;1440) — 에서는 예전 헤더 버튼과 똑같이 `/aiagent`
 * 탭으로 간다. 버튼은 <b>그래도 사라지지 않는다</b>: 못 하는 이유로 버튼을 감추면 사용자는 기능이
 * 없어진 줄 안다.
 */

/** three.js 는 첫 화면 묶음에 넣지 않는다 — 버튼은 어디에나 있지만 씬은 나중에 와도 된다. */
const HeroOrb = lazy(() => import('@/components/hero/HeroOrb').then((m) => ({ default: m.HeroOrb })));

/** 오브가 차지하는 칸. */
const BOX = 76;

/**
 * 씬에 넘기는 «목표 지름» — <b>칸보다 크게</b> 잡는다 (2026-09-18).
 *
 * <p>씬의 resize 는 목표를 칸 높이의 72% 로 눌러 둔다(낮은 칸에서 구가 칸을 넘지 않게 하는 장치).
 * 그래서 칸 크기를 그대로 넘기면 76px 칸에 <b>55px 짜리 구</b>가 서고, 둘레에 한 겹 여백이 남아
 * 구슬이 작아 보였다. 앱 웹뷰는 같은 상한을 캔버스를 1/0.72 로 키워 피했다(`AiOrb`) — 여기서는
 * 목표를 그만큼 키운다. 씬 쪽 값은 건드리지 않는다.
 *
 * <p>알갱이 보정도 <b>이 값</b>을 기준으로 잰다 — 앱 웹뷰가 재는 판(키운 캔버스)과 같은 크기여야
 * 두 화면의 톨 굵기가 같다. 칸 크기(76)로 재면 웹 쪽 톨만 굵어진다.
 */
const ORB_TARGET = BOX / 0.72;

/** 창 가장자리에서 오브 가운데가 지킬 최소 거리. 반지름 + 한 뼘이라 절반이 잘리지 않는다. */
const EDGE = BOX / 2 + 8;

/**
 * 씬이 돌 수 있는 기기인가. {@link HeroOrb} 는 「움직임 줄이기」거나 WebGL 을 못 열면 <b>빈 판</b>으로
 * 남는데, 배경을 끈 지금은 그 빈 판 뒤에 아무것도 없다 — <b>버튼이 통째로 사라진다.</b>
 * 그래서 같은 판정을 여기서 한 번 더 해, 못 도는 기기에는 CSS 로 그린 구슬을 대신 세운다
 * ({@code index.css} 의 {@code .ai-orb-still}).
 */
const CAN_RUN_SCENE = (() => {
  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return Boolean(document.createElement('canvas').getContext('webgl2'));
  } catch {
    return false;
  }
})();

/**
 * 이만큼 움직이면 <b>곧바로</b> 끌린다 — 누르고 기다리는 시간은 없다(사용자 지시 2026-09-29).
 *
 * <p>꾹 누르기(0.5초)를 시계로 재던 자리다. 되돌릴 수 없는 저장 앞의 «한 번 더» 였는데,
 * 옮기려는 사람에게는 매번 치르는 대가였고 <b>기다리는 동안 아무 일도 안 일어난다</b>는 것이
 * 고장처럼 읽혔다. 시계를 지우고, 누른 것과 끈 것을 <b>움직인 거리</b>로만 가른다.
 *
 * <p>0 이 아닌 이유: 이 오브의 본업은 눌리는 것이라, 누를 때 손이 1~2px 흔들리는 일은 흔하다.
 * 그만큼으로 자리가 저장되면 «누르기만 했는데 오브가 옮겨졌다» 가 된다. 4px 은 흔들린 손은
 * 걸러 내고 끌려는 손은 첫 프레임에 통과하는 폭이다.
 */
const DRAG_SLOP = 4;

/**
 * 이름표를 닫아 둔 사람인가. <b>개인의 선택</b>이라 브라우저에만 남는다(사용자 지시 2026-09-17) —
 * 「이게 AI Agent 라는 것」 은 한 번 알면 그만이고, 그 앎의 시점은 사람마다 다르다. 서버에 두면
 * 먼저 안 사람이 남의 화면에서 이름표를 걷는 셈이 된다.
 *
 * <p>글자 자체는 관리자가 정한다({@code OrbDesignModal}) — 무엇이라 부를지는 회사의 결정이고,
 * 그것을 계속 볼지는 개인의 결정이다.
 */
const CAP_DISMISSED_KEY = 'bomi.aiorb.cap-dismissed';

/**
 * 마지막으로 말을 건 시각(ms). <b>{@code sessionStorage} 다</b> — 탭 하나의 기억이다.
 *
 * <p>처음에는 {@code localStorage} 에 뒀는데, 그러면 <b>브라우저 전체가 25분 동안 조용해진다</b>:
 * 어느 탭에서 한 번 떴다는 이유로 새로 연 탭도 말을 안 한다(사용자 지적 2026-09-17).
 * 탭 기억이면 «새로 연 탭에서는 20초 뒤 한 번» 이 지켜지고, 새로고침으로 수다가 처음부터
 * 다시 시작되지도 않는다.
 */
const SPOKE_AT_KEY = 'bomi.aiorb.spoke-at';
/** 편집 권한이 없는 사람이 끌어 놓은 자리(비율 JSON). 이 브라우저에만 남는다. */
const PERSONAL_AT_KEY = 'bomi.aiorb.personal-at';

function readPersonalAt(raw: string | null): { xPct: number; yPct: number } | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { xPct?: unknown; yPct?: unknown };
    return typeof v.xPct === 'number' && typeof v.yPct === 'number' ? { xPct: v.xPct, yPct: v.yPct } : null;
  } catch {
    return null;
  }
}

/*
  말 거는 때(첫 말까지 · 다시 말하기까지)는 <b>여기 상수가 아니다</b> — 설정이다
  ({@code OrbDesign.sayDelaySec} · 사용자 지시 2026-09-17). 「3분이 적당한가」는 돌려 봐야 아는
  값이라, 상수로 두면 시험할 때마다 배포해야 하고 시험값을 되돌리는 것을 잊으면 전 직원이 그 값을
  쓴다. 실제로 5초를 박아 둘 뻔했다.
*/

/** 한 번 뜬 말풍선이 스스로 사라지기까지. 읽고 누를 만큼은 되고, 눌러 지울 만큼 길지는 않다. */
const SAY_MS = 8 * 1000;

/** 말풍선 폭. 꼬리를 오브 쪽으로 옮길 때 이 값이 기준이라 상수로 둔다. */
const SAY_WIDTH = 220;

/* 사생활 보호 모드에서는 저장소 접근 자체가 막힐 수 있다 — 기억을 못 할 뿐 말은 한다. */
function readSpokeAt(): string | null {
  try {
    return window.sessionStorage.getItem(SPOKE_AT_KEY);
  } catch {
    return null;
  }
}

function writeSpokeAt(at: number) {
  try {
    window.sessionStorage.setItem(SPOKE_AT_KEY, String(at));
  } catch {
    // 못 남기면 다음 조용한 구간을 못 지킨다. 그것뿐이다.
  }
}

export function AiAgentBubble({
  dockAvailable,
  dockOpen,
  onToggleDock,
  rightInset = 0,
}: {
  dockAvailable: boolean;
  dockOpen: boolean;
  onToggleDock: () => void;
  /** 열려 있는 도크가 오른쪽에서 먹은 폭. 오브가 그 밑으로 들어가지 않게 당길 때 쓴다. */
  rightInset?: number;
}) {
  const navigate = useNavigate();
  const open = dockAvailable && dockOpen;

  const { data } = useOrbDesign();
  const design = data ?? ORB_DESIGN_DEFAULT;
  const save = useSaveOrbDesign();
  /** 끌 수 있는 사람인가. 서버도 같은 판정으로 막는다({@code AgentOrbDesignController}). */
  const canPlace = useMenuCapability('ai_agent', 'edit');

  /*
    끄는 동안의 자리(px). 끝나면 비율로 바꿔 서버에 보내고 이 값을 버린다 — 저장된 값이 정본이라,
    들고 있으면 다른 탭에서 옮긴 자리가 이 화면에 반영되지 않는다.
  */
  const [dragAt, setDragAt] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  /**
   * 끄는 중의 상태. {@code grab} 은 <b>잡은 자리와 오브 가운데의 차</b>다 — 이것이 없으면
   * 끌기가 시작되는 순간 오브가 커서 밑으로 <b>순간이동</b>한다. 가장자리를 잡았든 가운데를
   * 잡았든, 잡은 그 지점을 쥔 채로 따라와야 «끄는» 것처럼 느껴진다.
   */
  const dragging = useRef<{
    id: number;
    at: { x: number; y: number };
    grab: { x: number; y: number };
    moved: boolean;
  } | null>(null);
  /**
   * 다음 프레임에 그릴 자리. 포인터는 <b>한 프레임에 여러 번</b> 오는데(120Hz 마우스·합쳐진
   * 터치 이벤트), 올 때마다 setState 를 하면 그리지도 못할 상태를 만드느라 프레임을 흘린다.
   * 마지막 자리만 rAF 한 번에 넘긴다.
   */
  const pending = useRef<{ x: number; y: number } | null>(null);
  const frame = useRef(0);
  /** 끌어 놓은 <b>직후</b>인가. 브라우저는 끌기가 끝나도 click 을 한 번 더 주는데, 그게 대화를 연다. */
  const justDragged = useRef(false);

  /* 창 크기. 비율을 px 로 바꾸는 데 필요하고, 창을 줄이면 오브도 따라 들어와야 한다. */
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const sync = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);

  /**
   * 화면 안으로 당긴다.
   *
   * <p>오른쪽 한계는 <b>열린 도크만큼 좁아진다</b>. 도크는 이 버튼보다 위에 서므로(z-index 210),
   * 당기지 않으면 끌어 둔 자리가 도크 밑이던 사람은 오브를 잃는다 — 닫을 손까지 함께.
   */
  const clampToView = useCallback(
    (x: number, y: number) => ({
      x: Math.min(Math.max(x, EDGE), Math.max(EDGE, viewport.w - rightInset - EDGE)),
      // 위쪽은 상단바(56px) 아래로 — 그 위에 서면 헤더 버튼을 가린다.
      y: Math.min(Math.max(y, 56 + EDGE), Math.max(56 + EDGE, viewport.h - EDGE)),
    }),
    [viewport, rightInset],
  );

  const [personalRaw, setPersonalRaw] = useStoredState(PERSONAL_AT_KEY);
  // 권한이 있는 사람은 회사 자리를 본다 — 예전에 권한 없이 놓아 둔 자리가 남아 있어도 무시한다.
  const position = (!canPlace && readPersonalAt(personalRaw)) || design.position;
  const placed = dragAt ?? {
    x: position.xPct * viewport.w,
    y: position.yPct * viewport.h,
  };
  /*
    ── 묻는 창 옆에 서기 ({@link useOrbAnchor} · 사용자 지시 2026-11-02) ───────────────────
    「작성 안내가 필요하신가요?」 같은 창이 뜨면 그 창(.modal-content)의 <b>좌상단 모서리</b>에 붙어
    오브가 묻는 것처럼 보인다. 창은 열리며 움직이고 창 크기도 바뀌므로 붙어 있는 동안은 매 프레임 잰다
    (바뀐 때만 그린다). 신호가 비면 저장된 자리로 돌아간다 — 저장값은 건드리지 않는다.
  */
  const anchorName = useOrbAnchor();
  const [anchorAt, setAnchorAt] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (!anchorName) {
      setAnchorAt(null);
      return;
    }
    let raf = 0;
    const track = () => {
      const node = document.querySelector<HTMLElement>(`[data-orb-anchor="${anchorName}"]`);
      const box = (node?.closest<HTMLElement>('.modal-content') ?? node)?.getBoundingClientRect();
      if (box && box.width > 0) {
        const x = Math.min(Math.max(box.left, EDGE), window.innerWidth - EDGE);
        const y = Math.min(Math.max(box.top, EDGE), window.innerHeight - EDGE);
        setAnchorAt((prev) => (prev && Math.abs(prev.x - x) < 0.5 && Math.abs(prev.y - y) < 0.5 ? prev : { x, y }));
      }
      raf = requestAnimationFrame(track);
    };
    raf = requestAnimationFrame(track);
    return () => cancelAnimationFrame(raf);
  }, [anchorName]);
  /** 붙으러 가고 돌아오는 동안만 미끄러진다. 끌 때까지 미끄러지면 손을 늦게 따라온다. */
  const [gliding, setGliding] = useState(false);
  const anchored = anchorAt !== null;
  useEffect(() => {
    setGliding(true);
    const timer = window.setTimeout(() => setGliding(false), 700);
    return () => window.clearTimeout(timer);
  }, [anchored]);
  const glide = gliding && !isDragging ? 'left 0.55s cubic-bezier(.2,.8,.2,1), top 0.55s cubic-bezier(.2,.8,.2,1)' : undefined;

  const at = anchorAt ?? clampToView(placed.x, placed.y);

  /** 끌기를 접는다 — 손을 뗐을 때도, 기기가 누르기를 가로챘을 때도 같은 길이다. */
  const endDrag = () => {
    cancelAnimationFrame(frame.current);
    frame.current = 0;
    pending.current = null;
    dragging.current = null;
    setIsDragging(false);
  };

  const onPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    // 창 옆에 붙어 있는 동안은 끌지 않는다 — 놓으면 그 자리가 저장되어 창이 닫힌 뒤에도 남는다.
    if (open || anchorAt) return;
    // 왼쪽 버튼만. 가운데·오른쪽 버튼으로 끌면 누른 적 없는 저장이 나간다.
    if (event.button !== 0) return;

    dragging.current = {
      id: event.pointerId,
      at: { x: event.clientX, y: event.clientY },
      // 잡은 자리에서 오브 가운데까지. 끄는 내내 이만큼 더해 «잡은 데를 쥔 채» 따라오게 한다.
      grab: { x: at.x - event.clientX, y: at.y - event.clientY },
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragging.current;
    if (!drag || drag.id !== event.pointerId) return;

    /*
      아직 흔들린 손일 수 있다 — 4px 을 넘기 전에는 자리를 바꾸지 않는다. 대신 <b>기다리지 않는다</b>:
      넘는 그 프레임에 바로 끌린다.
    */
    if (!drag.moved) {
      if (Math.hypot(event.clientX - drag.at.x, event.clientY - drag.at.y) <= DRAG_SLOP) return;
      drag.moved = true;
      // 끌기가 시작됐음을 손으로도 알린다(있는 기기에서만 — 없으면 아무 일도 일어나지 않는다).
      navigator.vibrate?.(12);
    }
    setIsDragging(true);
    pending.current = clampToView(event.clientX + drag.grab.x, event.clientY + drag.grab.y);
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      if (pending.current) setDragAt(pending.current);
    });
  };

  const onPointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragging.current;
    if (!drag || drag.id !== event.pointerId) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    const moved = drag.moved;
    const grab = drag.grab;
    endDrag();

    // 4px 을 못 넘겼으면 그냥 누른 것이다 — 그대로 대화가 열린다.
    if (!moved) return;
    justDragged.current = true;
    const next = clampToView(event.clientX + grab.x, event.clientY + grab.y);
    /*
      비율로 바꿔 저장한다. 창 크기가 다른 사람의 화면에서도 «오른쪽 아래 구석» 이 구석으로 남는다.
      도크가 열려 있었다면 그만큼 당겨진 자리를 그대로 저장하게 되는데, 그것이 맞다 — 그 사람이
      눈으로 보고 놓은 자리가 그 자리다.
    */
    const nextPct = { xPct: next.x / viewport.w, yPct: next.y / viewport.h };
    if (!canPlace) {
      setPersonalRaw(JSON.stringify(nextPct));
      setDragAt(null);
      return;
    }
    save.mutate({ ...design, position: nextPct });
    // 저장 응답이 캐시에 앉을 때까지는 방금 놓은 자리를 들고 있는다(놓자마자 되튀지 않게).
    setDragAt(next);
  };

  /* 서버 값이 바뀌면(내 저장이든 남의 저장이든) 그쪽이 정본이다. */
  useEffect(() => {
    if (!dragging.current) setDragAt(null);
  }, [design.position.xPct, design.position.yPct]);

  /* 끄는 채로 화면이 바뀌면 다음 프레임이 주인을 잃는다 — 떠날 때 함께 끈다. */
  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  // ── 이름표 ─────────────────────────────────────────────────────────────────
  const [capDismissed, setCapDismissed] = useStoredState(CAP_DISMISSED_KEY);
  const showCap = Boolean(design.label) && capDismissed !== '1' && !open && !anchorName;

  // ── 가끔 하는 말 ───────────────────────────────────────────────────────────
  //
  // 알림이 아니라 <b>혼잣말</b>이다. 그래서 「읽음」 도, 다시 보는 자리도 없다 — 놓쳐도 잃는 것이
  // 없어야 이 정도로 자주 띄울 수 있다.
  //
  // <b>무슨 말을 할지는 서버가 정한다</b>(사용자 지시 2026-09-18). 관리자가 적어 둔 세 줄을
  // 돌려 말하던 자리인데, 회사가 무엇을 적든 그 말은 오늘 이 사람과 무관했다 — 지금은 홈 인사말의
  // 신호 엔진이 회의·할일·결재·날씨·식단을 읽고 만든 묶음에서 고른다({@link useOrbLines}).
  //
  // <b>홈 인사말을 끈 사람에게는 말을 걸지 않는다</b>(사용자 지시 2026-09-18). 그 판단은 훅이
  // 쥐고 있고, 여기로는 {@code null} 로 내려온다 — 「말하지 말라」와 「할 말이 없다」를 가르는
  // 자리가 둘이면 언젠가 한쪽만 고쳐진다.
  const [say, setSay] = useState<string | null>(null);
  const orbLines = useOrbLines();

  /*
    시계를 <b>다시 감지 않기 위한</b> 손잡이들.

    <p>예전에는 효과가 `[open, lines]` 에 걸려 있었다. 그러면 도크를 여닫거나 설정 응답이 한 번
    더 오는 것만으로 <b>20초가 처음으로 되감긴다</b> — 20초 안에 무엇이든 한 번 바뀌면 말풍선은
    영영 안 뜬다(사용자 지적 2026-09-17).

    <p>말 묶음이 참조로 들어온 것은 <b>그 교훈의 연장</b>이다. 이 묶음은 십 분마다 스스로 다시
    온다({@code REFRESH_MS}) — 의존성에 넣으면 십 분마다 시계가 처음으로 되감기고, 그때부터
    오브는 「설정한 간격」이 아니라 「응답이 온 지 20초」에 맞춰 말하게 된다.
  */
  const openRef = useRef(open);
  openRef.current = open;
  const linesRef = useRef(orbLines);
  linesRef.current = orbLines;

  /**
   * 방금 한 말의 화제. 다음 차례에서 <b>같은 화제를 다시 고르지 않으려고</b> 들고 있는다 —
   * 「남은 회의가 2건」 뒤에 「회의는 잘 하셨나요」 가 붙으면 두 번 말한 것처럼 읽힌다.
   */
  const lastSignalRef = useRef<string | null>(null);

  /* 말 거는 때는 설정이다. 바뀌면 시계를 <b>다시 감아야</b> 하므로 여기는 참조가 아니라 의존성이다. */
  const { sayDelaySec, sayEverySec } = design;

  useEffect(() => {
    const quietMs = Math.max(1, sayEverySec) * 1000;
    let hide = 0;
    const speak = () => {
      // 대화가 이미 열려 있으면 말을 걸지 않는다 — 옆에서 답하고 있는데 「도와드릴까요?」는 무례하다.
      if (openRef.current) return;
      // 화면 안내(?)가 떠 있는 동안에도 말을 걸지 않는다 — 말풍선이 안내 카드 옆을 가린다(2026-11-02).
      if (document.getElementById('tour-step-title')) return;

      const last = Number(readSpokeAt() ?? 0);
      // `last` 가 0 이면 <b>이 탭에서 처음</b>이다 — 조용한 구간을 따지지 않고 말한다.
      if (last && Date.now() - last < quietMs) return;

      /*
        고르는 일은 <b>말하려는 순간</b>에 한다. 미리 뽑아 두면 화면을 켜 둔 채 자리를 비운
        사람에게 11시 58분의 식단 이야기가 오후 1시에 나간다 — 묶음은 십 분마다 새로 오지만
        그 안에서 어느 줄이 나갈지는 지금 정해야 한다.

        <p>못 받았으면 기본 문장으로 말한다 — <b>서버 사정이 침묵이 되지는 않는다</b>(2026-09-17
        의 사고). 반대로 <b>사람이 껐으면</b> 기본 문장으로도 물러서지 않고 조용히 있는다.
        그 둘을 가르는 일은 {@link useOrbLines} 가 한다.
      */
      const line = pickOrbLine(linesRef.current, lastSignalRef.current);
      if (!line) return;

      writeSpokeAt(Date.now());
      lastSignalRef.current = line.signal;
      setSay(line.text);
      hide = window.setTimeout(() => setSay(null), SAY_MS);
    };

    const first = window.setTimeout(speak, Math.max(0, sayDelaySec) * 1000);
    // 화면을 켜 둔 채 일하는 사람에게도 이따금 돌아온다. 조용한 구간은 `speak` 가 다시 잰다.
    const again = window.setInterval(speak, quietMs);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(again);
      window.clearTimeout(hide);
    };
  }, [sayDelaySec, sayEverySec]);

  // 끄는 동안에는 말풍선을 치운다 — 손을 따라다니는 말풍선은 끌기를 방해한다.
  // 도크를 열었을 때도 치운다 — 대답할 자리가 이미 펼쳐졌는데 말풍선이 남아 있을 이유가 없다.
  useEffect(() => {
    if (isDragging || open) setSay(null);
  }, [isDragging, open]);

  const size = open ? 58 : BOX;
  /** 구체의 반지름. 칸(76px)이 아니라 <b>보이는 구슬</b>이 기준이어야 이름표·말풍선이 머리에 붙는다. */
  const radius = size * 0.36;

  /*
    말풍선의 가로 자리. 오브는 어디로든 끌려 가므로 구석에서는 <b>말풍선이 화면 밖으로 나간다</b>.
    판은 화면 안으로 당기고, 꼬리만 오브 쪽으로 옮겨 «누가 말하는지» 를 남긴다.
  */
  const sayLeft = Math.min(
    Math.max(at.x, 12 + SAY_WIDTH / 2),
    Math.max(12 + SAY_WIDTH / 2, viewport.w - rightInset - 12 - SAY_WIDTH / 2),
  );
  const tailLeft = Math.min(Math.max(at.x - (sayLeft - SAY_WIDTH / 2), 18), SAY_WIDTH - 18);

  return (
    <>
      {/*
        머리 위의 이름표. 구슬만 떠 있으면 <b>이게 AI Agent 라는 것을 아무도 모른다</b>
        (사용자 지적 2026-09-17) — 말풍선 아이콘을 지운 대가다.

        <p>도크가 열려 있을 때는 빼는데, 그때 오브는 <b>닫는 손</b>(×)이라 이름표가 「AI Agent 를
        연다」로 읽히기 때문이다. 버튼 밖에 서는 이유는 {@code index.css} 의 {@code .ai-orb-cap}.
      */}
      {/*
        끄는 중이다 — <b>지금 잡고 있다</b>를 둘레의 고리로 보여 준다(사용자 지시 2026-09-17).
        글로 적지 않는다: 이 신호는 잡고 있는 잠깐 동안만 사는 것이라, 읽을 겨를이 없다.
        오브보다 한 뼘 크게 두른다(`+26`) — 구체에 붙으면 테두리 빛과 섞여 안 읽힌다.
      */}
      {isDragging && (
        <span
          className="ai-orb-ring no-print"
          aria-hidden
          style={{ left: `${at.x}px`, top: `${at.y}px`, width: `${size + 26}px`, height: `${size + 26}px` }}
        />
      )}

      {/*
        오브가 <b>가끔 혼자 하는 말</b>(사용자 지시 2026-09-17). 알림이 아니라 혼잣말이라 「읽음」도
        다시 보는 자리도 없다 — 놓쳐도 잃는 것이 없어야 이 정도로 자주 띄울 수 있다.
        누르면 대화가 열린다: 말을 걸어 놓고 <b>대답할 자리가 없으면</b> 그냥 방해다.
      */}
      {say && !open && !anchorName && (
        <button
          type="button"
          className="ai-orb-say no-print"
          style={{ left: `${sayLeft}px`, top: `${at.y - radius - 12}px`, width: `${SAY_WIDTH}px` }}
          onClick={() => {
            setSay(null);
            if (dockAvailable) onToggleDock();
            else navigate('/aiagent');
          }}
        >
          {say}
          <span className="ai-orb-say-tail" style={{ left: `${tailLeft}px` }} aria-hidden />
        </button>
      )}

      {/*
        머리 위의 이름표. 구슬만 떠 있으면 <b>이게 AI Agent 라는 것을 아무도 모른다</b>
        (사용자 지적 2026-09-17) — 말풍선 아이콘을 지운 대가다.

        <p>말하고 있는 동안에는 <b>이름표가 물러난다</b>. 둘 다 머리 위에 서는 데다, 이름을 알리는
        일은 말풍선이 더 잘 한다. 도크가 열려 있을 때도 뺀다 — 그때 오브는 닫는 손(×)이라
        이름표가 「AI Agent 를 연다」로 읽힌다.
      */}
      {showCap && !say && !isDragging && (
        <span
          className="ai-orb-cap no-print"
          /*
            구체의 <b>머리</b>에 얹는다 — 칸의 위가 아니다. 칸(76px)보다 구체가 작아서(≈55px)
            칸 위에 맞추면 이름표와 구슬 사이가 한 뼘 뜬다.
          */
          style={{ left: `${at.x}px`, top: `${at.y - radius - 5}px` }}
        >
          {design.label}
          {/*
            닫으면 <b>이 브라우저에서만</b> 사라진다. 한 번 알면 그만인 이름표라, 아는 사람부터
            치울 수 있어야 한다. 되살릴 길은 두지 않는다 — 그걸 두려면 설정 화면에 줄 하나가 더
            늘고, 이름표를 다시 보고 싶은 사람은 없다.
          */}
          <button
            type="button"
            className="ai-orb-cap-x"
            aria-label="이름표 닫기"
            title="이름표 닫기"
            onClick={() => setCapDismissed('1')}
          >
            ×
          </button>
        </span>
      )}
      <button
        type="button"
        className={[
          'ai-orb no-print',
          !open ? 'is-draggable' : '',
          isDragging ? 'is-dragging' : '',
        ].filter(Boolean).join(' ')}
        data-tour="fab-ai-agent"
        style={{ left: `${at.x}px`, top: `${at.y}px`, width: `${size}px`, height: `${size}px`, transition: glide }}
        aria-expanded={dockAvailable ? dockOpen : undefined}
        aria-label={open ? 'AI Agent 닫기' : 'AI Agent 열기'}
        title={open ? 'AI Agent 닫기' : 'AI Agent 에게 물어보기 (끌면 자리 옮기기)'}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        // 기기가 누르기를 가로챘다(화면 굴리기·전화). 남겨 두면 그다음 움직임에 오브가 따라붙는다.
        onPointerCancel={endDrag}
        onClick={() => {
          // 끌어 놓은 직후의 click 은 삼킨다 — 자리만 옮겼는데 대화가 열리면 두 번 놀란다.
          if (justDragged.current) {
            justDragged.current = false;
            return;
          }
          if (dockAvailable) onToggleDock();
          else navigate('/aiagent');
        }}
      >
        {open ? (
          // 닫을 때만 ×가 뜬다. 평소에는 아무 그림도 없다 — 구체 자체가 표시다.
          <span className="ai-orb-face">
            <svg
              width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </span>
        ) : CAN_RUN_SCENE ? (
          <span className="ai-orb-scene" aria-hidden>
            {/* 받아 오는 동안은 아무것도 없다 — 씬이 뜨기 전 한 프레임을 위해 다른 그림을 깔면
                그 그림이 캔버스와 어긋나 한 번 «바뀌는» 것처럼 보인다. */}
            <Suspense fallback={null}>
              <HeroOrb
                look={toLook(design)}
                orbSize={ORB_TARGET}
                particleSize={particleSizeFor(ORB_TARGET)}
                background={false}
                flare={false}
              />
            </Suspense>
          </span>
        ) : (
          <span className="ai-orb-still" aria-hidden />
        )}
      </button>
    </>
  );
}
